import { useState, useEffect, useMemo } from 'react';
import { Save, Filter, Target, Zap, AlertTriangle, Clock, Loader2, Plus, Trash2, Info } from 'lucide-react';
import Modal from '../common/Modal';
import { usePermissions } from '../../hooks/usePermissions';
import edgeService from '../../services/edge.service';
import routingPolicyService from '../../services/routingPolicy.service';
import type { RoutableSubnet, EdgeDestinations } from '../../services/routingPolicy.service';
import type { RoutingPolicyRule, RuleCreateData, ApplicationDefinition, ApplicationGroup } from '../../services';

interface LogicalInterface {
  id: string;
  name: string;         // logical_name ("WAN-Ethernet")
  portName: string;      // port_name ("G0") — agent-compatible
  role: string;
  ipv4?: string;
}

interface RuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: RuleCreateData) => void;
  rule?: RoutingPolicyRule | null;
  applications: ApplicationDefinition[];
  appGroups?: ApplicationGroup[];
  mode: 'add' | 'edit';
  isTemplate?: boolean;
  edgeId?: string;
  policyScope?: 'global' | 'edge' | 'device_group' | 'dg_template';
  tenantId?: string;
  defaultAction?: 'allow' | 'deny';
}

/** Validate CIDR notation: X.X.X.X/N where N is 0-32 */
function isValidCIDR(value: string): boolean {
  if (!value) return true; // optional field
  const match = value.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})\/(\d{1,2})$/);
  if (!match) return false;
  const octets = [+match[1], +match[2], +match[3], +match[4]];
  const prefix = +match[5];
  return octets.every(o => o >= 0 && o <= 255) && prefix >= 0 && prefix <= 32;
}

/** Validate IPv4 address: X.X.X.X */
function isValidIPv4(value: string): boolean {
  if (!value) return false;
  const match = value.match(/^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/);
  if (!match) return false;
  return [+match[1], +match[2], +match[3], +match[4]].every(o => o >= 0 && o <= 255);
}

const ALL_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

/** Parse a time_window string like "Mon,Tue,Wed 09:00-17:00" into structured form */
function parseTimeWindow(tw: string): { days: string[]; startTime: string; endTime: string } {
  if (!tw) return { days: [], startTime: '', endTime: '' };

  const match = tw.match(/^([\w,]+)\s+(\d{2}:\d{2})-(\d{2}:\d{2})$/);
  if (!match) return { days: [], startTime: '', endTime: '' };

  const days = match[1].split(',').filter(d => ALL_DAYS.includes(d as any));
  return { days, startTime: match[2], endTime: match[3] };
}

/** Build a time_window string from structured form */
function buildTimeWindow(days: string[], startTime: string, endTime: string): string {
  if (days.length === 0 || !startTime || !endTime) return '';
  return `${days.join(',')} ${startTime}-${endTime}`;
}

type MatchMode = 'none' | 'application' | 'group';

const RuleModal = ({ isOpen, onClose, onSave, rule, applications, appGroups = [], mode, isTemplate = true, edgeId, policyScope, tenantId, defaultAction }: RuleModalProps) => {
  const isDgScope = policyScope === 'device_group' || policyScope === 'dg_template';
  const { canCreate, canUpdate } = usePermissions();
  const [logicalInterfaces, setLogicalInterfaces] = useState<LogicalInterface[]>([]);
  const [routableSubnets, setRoutableSubnets] = useState<RoutableSubnet[]>([]);
  const [useCustomCidr, setUseCustomCidr] = useState(false);
  const [destinations, setDestinations] = useState<EdgeDestinations | null>(null);
  const [destinationsLoading, setDestinationsLoading] = useState(false);
  const [formData, setFormData] = useState<RuleCreateData>({
    app_definition_id: undefined,
    app_group_id: undefined,
    source_subnet: '',
    destination_subnet: '',
    time_window: '',
    action_type: 'route_via_interface',
    action_target: '',
    qos_profile: undefined,
    bandwidth_limit: undefined,
    dscp_match: undefined,
    dscp_mark: undefined,
    fallback_target: undefined,
    load_balance_config: undefined,
    rule_order: undefined,
    enabled: 1,
  });

  const [matchMode, setMatchMode] = useState<MatchMode>('none');
  const [twDays, setTwDays] = useState<string[]>([]);
  const [twStartTime, setTwStartTime] = useState('');
  const [twEndTime, setTwEndTime] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setErrors({});
      // Reset custom CIDR toggle — will be re-evaluated once destinations load
      setUseCustomCidr(false);
      if (rule) {
        setFormData({
          app_definition_id: rule.app_definition_id,
          app_group_id: rule.app_group_id,
          source_subnet: rule.source_subnet || '',
          destination_subnet: rule.destination_subnet || '',
          time_window: rule.time_window || '',
          action_type: rule.action_type,
          action_target: rule.action_target || '',
          qos_profile: rule.qos_profile,
          bandwidth_limit: rule.bandwidth_limit,
          dscp_match: rule.dscp_match,
          dscp_mark: rule.dscp_mark,
          fallback_target: rule.fallback_target,
          load_balance_config: rule.load_balance_config,
          rule_order: rule.rule_order,
          enabled: rule.enabled,
        });
        // Set match mode from existing rule
        if (rule.app_definition_id) setMatchMode('application');
        else if (rule.app_group_id) setMatchMode('group');
        else setMatchMode('none');
        // Parse time window
        const tw = parseTimeWindow(rule.time_window || '');
        setTwDays(tw.days);
        setTwStartTime(tw.startTime);
        setTwEndTime(tw.endTime);
      } else {
        setFormData({
          app_definition_id: undefined,
          app_group_id: undefined,
          source_subnet: '',
          destination_subnet: '',
          time_window: '',
          action_type: isDgScope ? (defaultAction === 'deny' ? 'allow' : 'drop') : 'route_via_interface',
          action_target: '',
          qos_profile: undefined,
          bandwidth_limit: undefined,
          dscp_match: undefined,
          dscp_mark: undefined,
          fallback_target: undefined,
          rule_order: undefined,
          enabled: 1,
        });
        setMatchMode('none');
        setTwDays([]);
        setTwStartTime('');
        setTwEndTime('');
      }
    } else {
      // Reset state on close to prevent stale data on next open
      setDestinations(null);
      setRoutableSubnets([]);
    }
  }, [isOpen, rule]);

  // Re-evaluate custom CIDR toggle when destinations or subnets load
  useEffect(() => {
    if (!isOpen || !rule?.destination_subnet) return;
    const isKnownInDestinations = destinations?.subnets.some(s => s.subnet === rule.destination_subnet);
    const isKnownInRoutable = routableSubnets.some(s => s.subnet === rule.destination_subnet);
    const isPeerSentinel = rule.destination_subnet.startsWith('peer:');
    setUseCustomCidr(!isKnownInDestinations && !isKnownInRoutable && !isPeerSentinel);
  }, [destinations, routableSubnets]);

  // Sync time window string when day/time changes
  useEffect(() => {
    const tw = buildTimeWindow(twDays, twStartTime, twEndTime);
    setFormData(prev => ({ ...prev, time_window: tw }));
  }, [twDays, twStartTime, twEndTime]);

  // Fetch edge destinations (unified context-aware endpoint)
  useEffect(() => {
    const fetchDestinations = async () => {
      if (!isOpen) return;
      if (edgeId && !isTemplate) {
        // Edge-specific or DG with edge binding — fetch full context
        setDestinationsLoading(true);
        try {
          const dest = await routingPolicyService.getEdgeDestinations(edgeId);
          setDestinations(dest);
          // Also populate legacy logicalInterfaces for LB config compatibility
          setLogicalInterfaces(dest.interfaces.map(i => ({
            id: i.name,
            name: i.label.split(' (')[0] || i.name,
            portName: i.name,
            role: i.role,
            ipv4: i.ipv4,
          })));
        } catch (err) {
          console.error('Failed to fetch edge destinations:', err);
          setDestinations(null);
          // Fallback: try legacy interface fetch
          try {
            const response = await edgeService.listLogicalInterfaces(edgeId);
            setLogicalInterfaces((response.items || []).map((iface: any) => ({
              ...iface,
              portName: iface.physicalInterface || iface.name,
            })));
          } catch (fallbackErr) { console.error('Failed to fetch legacy interfaces:', fallbackErr); }
        } finally {
          setDestinationsLoading(false);
        }
      } else if (isDgScope && tenantId && !edgeId) {
        // DG without edge — fall back to tenant-wide routable subnets
        setDestinationsLoading(true);
        try {
          const subnets = await routingPolicyService.getRoutableSubnets(tenantId);
          setRoutableSubnets(subnets);
        } catch (err) {
          console.error('Failed to fetch routable subnets:', err);
        } finally {
          setDestinationsLoading(false);
        }
      }
    };
    fetchDestinations();
  }, [isOpen, edgeId, isTemplate, isDgScope, tenantId]);

  const routingInterfaces = useMemo(() =>
    logicalInterfaces.filter(iface => iface.role === 'WAN' || iface.role === 'LAN'),
    [logicalInterfaces]
  );

  // Dynamic tunnel list from destinations API (falls back to static list)
  const availableTunnels = useMemo(() => {
    if (destinations) {
      return destinations.tunnels;
    }
    // Static fallback when destinations not loaded
    return [
      { name: 'wg0', label: 'wg0 (IoT/Connector)', configured: true },
      { name: 'wg1', label: 'wg1 (App VPN — WireGuard)', configured: true },
      { name: 'host-xfrm-apps', label: 'host-xfrm-apps (IKEv2 App VPN)', configured: true },
      { name: 'wg2', label: 'wg2 (E2E Peering)', configured: true },
    ];
  }, [destinations]);

  // Group subnets by category for the categorized dropdown
  const categorizedSubnets = useMemo(() => {
    if (!destinations) return null;
    const groups: Record<string, { subnet: string; label: string }[]> = {};
    for (const s of destinations.subnets) {
      const key = s.source || 'Other';
      if (!groups[key]) groups[key] = [];
      const parts = [s.subnet];
      if (s.peerName) parts.push(s.peerName);
      else if (s.sourceName) parts.push(s.sourceName);
      groups[key].push({
        subnet: s.subnet,
        label: `${s.subnet} — ${s.peerName || s.sourceName || s.source || 'Unknown'}`,
      });
    }
    return groups;
  }, [destinations]);

  const handleChange = (field: keyof RuleCreateData, value: any) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      // Clear action_target when switching action_type (prevents stale values)
      if (field === 'action_type' && value !== prev.action_type) {
        next.action_target = '';
      }
      return next;
    });
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const handleMatchModeChange = (newMode: MatchMode) => {
    setMatchMode(newMode);
    // Clear both selections when switching
    setFormData(prev => ({
      ...prev,
      app_definition_id: undefined,
      app_group_id: undefined,
    }));
  };

  const toggleDay = (day: string) => {
    setTwDays(prev =>
      prev.includes(day) ? prev.filter(d => d !== day) : [...prev, day]
    );
  };

  const selectWeekdays = () => setTwDays(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const selectAllDays = () => setTwDays([...ALL_DAYS]);
  const clearDays = () => { setTwDays([]); setTwStartTime(''); setTwEndTime(''); };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (formData.source_subnet && !isValidCIDR(formData.source_subnet)) {
      newErrors.source_subnet = 'Invalid CIDR format (e.g., 10.0.0.0/24)';
    }
    if (formData.destination_subnet && !formData.destination_subnet.startsWith('peer:') && !isValidCIDR(formData.destination_subnet)) {
      newErrors.destination_subnet = 'Invalid CIDR format (e.g., 0.0.0.0/0)';
    }

    const requiresTarget = ['route_via_interface', 'route_via_nexthop', 'route_via_tunnel'].includes(formData.action_type);
    if (!isTemplate && requiresTarget && !(formData.action_target || '').trim()) {
      newErrors.action_target = 'Action target is required for this action type';
    }

    // Validate nexthop is a valid IPv4 address
    if (!isTemplate && formData.action_type === 'route_via_nexthop' && formData.action_target) {
      if (!isValidIPv4(formData.action_target)) {
        newErrors.action_target = 'Must be a valid IPv4 address (e.g., 192.168.1.1)';
      }
    }

    if (formData.action_type === 'rate_limit' && (!formData.bandwidth_limit || formData.bandwidth_limit < 1)) {
      newErrors.bandwidth_limit = 'Bandwidth limit is required for rate limiting';
    }

    if (formData.action_type === 'load_balance') {
      const lbc = formData.load_balance_config as any;
      if (!lbc?.interfaces || lbc.interfaces.length < 2) {
        newErrors.load_balance_config = 'At least 2 interfaces required for load balancing';
      }
    }

    // Validate time window consistency
    if (twDays.length > 0 && (!twStartTime || !twEndTime)) {
      newErrors.time_window = 'Both start and end times are required when days are selected';
    }
    if (twStartTime && twEndTime && twStartTime >= twEndTime) {
      newErrors.time_window = 'End time must be after start time';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    onSave(formData);
  };

  const requiresTarget = ['route_via_interface', 'route_via_nexthop', 'route_via_tunnel'].includes(formData.action_type);
  const requiresBandwidth = formData.action_type === 'rate_limit';
  const isLoadBalance = formData.action_type === 'load_balance';

  // Load balance config helpers
  const lbInterfaces = (formData.load_balance_config as any)?.interfaces || [];
  const lbAlgorithm = (formData.load_balance_config as any)?.algorithm || 'round_robin';

  const updateLoadBalanceConfig = (interfaces: { name: string; weight: number }[], algorithm: string) => {
    handleChange('load_balance_config', { interfaces, algorithm });
  };

  const addLBInterface = () => {
    updateLoadBalanceConfig([...lbInterfaces, { name: '', weight: 1 }], lbAlgorithm);
  };

  const removeLBInterface = (index: number) => {
    updateLoadBalanceConfig(lbInterfaces.filter((_: any, i: number) => i !== index), lbAlgorithm);
  };

  const updateLBInterface = (index: number, field: 'name' | 'weight', value: string | number) => {
    const updated = lbInterfaces.map((iface: any, i: number) =>
      i === index ? { ...iface, [field]: value } : iface
    );
    updateLoadBalanceConfig(updated, lbAlgorithm);
  };

  // Sort applications alphabetically, deduplicate by name
  // DG access policies are L3/L4 only — hide domain/DPI apps
  const sortedApps = useMemo(() => {
    let filtered = [...applications].filter(app => app.enabled === 1);
    if (isDgScope) {
      filtered = filtered.filter(app =>
        !app.detection_method || app.detection_method === 'port' || app.detection_method === 'ip_range'
      );
    }
    return filtered
      .reduce((acc, app) => {
        if (!acc.some(a => a.name === app.name)) acc.push(app);
        return acc;
      }, [] as ApplicationDefinition[])
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [applications, isDgScope]);

  // Filter groups that have at least 1 member, sort alphabetically
  const sortedGroups = useMemo(() => [...appGroups]
    .filter(g => g.enabled === 1 && (g.memberCount || 0) > 0)
    .sort((a, b) => a.name.localeCompare(b.name)),
    [appGroups]
  );

  const footer = (
    <div className="flex items-center justify-end space-x-3">
      <button type="button" onClick={onClose} className="btn-secondary">
        Cancel
      </button>
      {(mode === 'add' ? canCreate : canUpdate) && (
        <button form="rule-form" type="submit" className="btn-primary inline-flex items-center space-x-2">
          <Save className="w-4 h-4" />
          <span>{mode === 'add' ? 'Add Rule' : 'Save Changes'}</span>
        </button>
      )}
    </div>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'add' ? (isDgScope ? 'Add Access Rule' : 'Add Routing Rule') : (isDgScope ? 'Edit Access Rule' : 'Edit Routing Rule')}
      size="xl"
      footer={footer}
    >
      <form id="rule-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Match Criteria */}
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Filter className="w-4 h-4 mr-2 text-primary-500" />
            Match Criteria
          </h4>

          {/* L3/L4 note for DG access policies */}
          {isDgScope && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-xs text-blue-700 flex items-start space-x-2">
              <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>Application matching for access policies uses L3/L4 only (protocol + ports). Domain-based applications are hidden.</span>
            </div>
          )}

          {/* Application or Group selector */}
          <div className="space-y-3 mb-6">
            <label className="block text-sm font-medium text-gray-700">Match Traffic By</label>
            <div className="flex items-center space-x-4">
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="matchMode"
                  checked={matchMode === 'none'}
                  onChange={() => handleMatchModeChange('none')}
                  className="form-radio text-primary-600 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700">Any Traffic</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="matchMode"
                  checked={matchMode === 'application'}
                  onChange={() => handleMatchModeChange('application')}
                  className="form-radio text-primary-600 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700">Application</span>
              </label>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="radio"
                  name="matchMode"
                  checked={matchMode === 'group'}
                  onChange={() => handleMatchModeChange('group')}
                  className="form-radio text-primary-600 h-4 w-4"
                />
                <span className="ml-2 text-sm text-gray-700">Application Group</span>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Application dropdown - only when matchMode is 'application' */}
            {matchMode === 'application' && (
              <div className="space-y-2 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Application</label>
                <select
                  value={formData.app_definition_id || ''}
                  onChange={(e) => handleChange('app_definition_id', e.target.value || undefined)}
                  className="input-base w-full"
                >
                  <option value="">Select an application...</option>
                  {sortedApps.map(app => (
                    <option key={app.id} value={app.id}>
                      {app.name} ({app.protocol?.toUpperCase() || 'ANY'}{app.destination_ports ? ` : ${app.destination_ports}` : ''})
                    </option>
                  ))}
                </select>
                {sortedApps.length === 0 && (
                  <p className="text-xs text-amber-600">
                    No applications found. Use "Update Defaults" on the Policy Templates page to populate.
                  </p>
                )}
              </div>
            )}

            {/* Application Group dropdown - only when matchMode is 'group' */}
            {matchMode === 'group' && (
              <div className="space-y-2 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700">Application Group</label>
                <select
                  value={formData.app_group_id || ''}
                  onChange={(e) => handleChange('app_group_id', e.target.value || undefined)}
                  className="input-base w-full"
                >
                  <option value="">Select an application group...</option>
                  {sortedGroups.map(group => (
                    <option key={group.id} value={group.id}>
                      {group.name} ({group.memberCount || 0} apps)
                    </option>
                  ))}
                </select>
                {sortedGroups.length === 0 && (
                  <p className="text-xs text-amber-600">
                    No application groups found. Use "Update Defaults" on the Policy Templates page to populate.
                  </p>
                )}
              </div>
            )}

            {/* Destination Subnet */}
            <div className="space-y-2 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700">
                Destination Subnet
              </label>
              {isTemplate && !isDgScope && !edgeId ? (
                <input
                  type="text"
                  value="Set when applied to Edge"
                  disabled
                  className="input-base w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                />
              ) : categorizedSubnets ? (
                /* Categorized dropdown from edge destinations API */
                <>
                  {!useCustomCidr ? (
                    <select
                      value={formData.destination_subnet}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setUseCustomCidr(true);
                          handleChange('destination_subnet', '');
                        } else {
                          handleChange('destination_subnet', e.target.value);
                        }
                      }}
                      className={`input-base w-full ${errors.destination_subnet ? 'border-red-300 focus:ring-red-500' : ''}`}
                      disabled={destinationsLoading}
                    >
                      <option value="">Any destination</option>
                      {categorizedSubnets['WAN Interface'] && (
                        <optgroup label="WAN Interfaces">
                          {categorizedSubnets['WAN Interface'].map((s, i) => (
                            <option key={`wan-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {categorizedSubnets['LAN Interface'] && (
                        <optgroup label="LAN Interfaces">
                          {categorizedSubnets['LAN Interface'].map((s, i) => (
                            <option key={`lan-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {categorizedSubnets['E2E Peering'] && (
                        <optgroup label="E2E Peering">
                          {destinations?.meta.hasPeering && (
                            <option value="peer:*">Any Peer — all learned routes</option>
                          )}
                          {categorizedSubnets['E2E Peering'].map((s, i) => (
                            <option key={`peer-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {categorizedSubnets['Static Route'] && (
                        <optgroup label="Static Routes">
                          {categorizedSubnets['Static Route'].map((s, i) => (
                            <option key={`sr-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {categorizedSubnets['Connector'] && (
                        <optgroup label="Connectors">
                          {categorizedSubnets['Connector'].map((s, i) => (
                            <option key={`conn-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {categorizedSubnets['Internet / Default Route'] && (
                        <optgroup label="Special">
                          {categorizedSubnets['Internet / Default Route'].map((s, i) => (
                            <option key={`special-${i}`} value={s.subnet}>{s.label}</option>
                          ))}
                        </optgroup>
                      )}
                      {Object.entries(categorizedSubnets)
                        .filter(([key]) => !['WAN Interface', 'LAN Interface', 'E2E Peering', 'Static Route', 'Connector', 'Internet / Default Route'].includes(key))
                        .map(([key, items]) => (
                          <optgroup key={key} label={key}>
                            {items.map((s, i) => (
                              <option key={`other-${key}-${i}`} value={s.subnet}>{s.label}</option>
                            ))}
                          </optgroup>
                        ))
                      }
                      <option value="__custom__">Custom CIDR...</option>
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={formData.destination_subnet}
                        onChange={(e) => handleChange('destination_subnet', e.target.value)}
                        placeholder="e.g., 192.168.1.0/24"
                        className={`input-base flex-1 ${errors.destination_subnet ? 'border-red-300 focus:ring-red-500' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setUseCustomCidr(false)}
                        className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
                      >
                        Use dropdown
                      </button>
                    </div>
                  )}
                  {destinationsLoading && (
                    <p className="text-xs text-gray-500 flex items-center">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading destinations from edge...
                    </p>
                  )}
                </>
              ) : routableSubnets.length > 0 || (isDgScope && !edgeId) ? (
                /* Legacy flat dropdown (DG without edge binding) */
                <>
                  {!useCustomCidr ? (
                    <select
                      value={formData.destination_subnet}
                      onChange={(e) => {
                        if (e.target.value === '__custom__') {
                          setUseCustomCidr(true);
                          handleChange('destination_subnet', '');
                        } else {
                          handleChange('destination_subnet', e.target.value);
                        }
                      }}
                      className={`input-base w-full ${errors.destination_subnet ? 'border-red-300 focus:ring-red-500' : ''}`}
                      disabled={destinationsLoading}
                    >
                      <option value="">Any destination</option>
                      {routableSubnets.length > 0 && (
                        <optgroup label="Available Subnets">
                          {routableSubnets.map((s, idx) => (
                            <option key={idx} value={s.subnet}>
                              {s.subnet} — {s.source} ({s.edgeNames.join(', ')})
                            </option>
                          ))}
                        </optgroup>
                      )}
                      <option value="__custom__">Custom CIDR...</option>
                    </select>
                  ) : (
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={formData.destination_subnet}
                        onChange={(e) => handleChange('destination_subnet', e.target.value)}
                        placeholder="e.g., 192.168.1.0/24"
                        className={`input-base flex-1 ${errors.destination_subnet ? 'border-red-300 focus:ring-red-500' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setUseCustomCidr(false)}
                        className="text-xs text-primary-600 hover:text-primary-700 whitespace-nowrap"
                      >
                        Use dropdown
                      </button>
                    </div>
                  )}
                  {destinationsLoading && (
                    <p className="text-xs text-gray-500 flex items-center">
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" /> Loading subnets from edges...
                    </p>
                  )}
                </>
              ) : (
                <input
                  type="text"
                  value={formData.destination_subnet}
                  onChange={(e) => handleChange('destination_subnet', e.target.value)}
                  placeholder="e.g., 0.0.0.0/0 or leave empty for any"
                  className={`input-base w-full ${errors.destination_subnet ? 'border-red-300 focus:ring-red-500' : ''}`}
                />
              )}
              {errors.destination_subnet ? (
                <p className="text-xs text-red-600 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" />{errors.destination_subnet}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {isTemplate && !isDgScope && !edgeId ? 'Configured per edge when template is applied'
                    : isDgScope ? 'Network resources this rule controls access to'
                    : 'CIDR notation (e.g., 8.8.8.0/24)'}
                </p>
              )}
            </div>

            {/* Time Window - Structured Controls */}
            <div className="space-y-3 md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 flex items-center">
                <Clock className="w-4 h-4 mr-1 text-gray-400" />
                Time Window
              </label>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                {/* Day selection */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-gray-600">Active Days</span>
                    <div className="flex items-center space-x-2">
                      <button type="button" onClick={selectWeekdays} className="text-xs text-primary-600 hover:text-primary-700">
                        Weekdays
                      </button>
                      <span className="text-gray-300">|</span>
                      <button type="button" onClick={selectAllDays} className="text-xs text-primary-600 hover:text-primary-700">
                        All Days
                      </button>
                      <span className="text-gray-300">|</span>
                      <button type="button" onClick={clearDays} className="text-xs text-gray-500 hover:text-gray-700">
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    {ALL_DAYS.map(day => (
                      <button
                        key={day}
                        type="button"
                        onClick={() => toggleDay(day)}
                        className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                          twDays.includes(day)
                            ? 'bg-primary-600 text-white'
                            : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {day}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Time range */}
                <div className="flex items-center space-x-3">
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={twStartTime}
                      onChange={(e) => setTwStartTime(e.target.value)}
                      className="input-base w-full text-sm"
                      disabled={twDays.length === 0}
                    />
                  </div>
                  <span className="text-gray-400 mt-5">to</span>
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-gray-600 mb-1">End Time</label>
                    <input
                      type="time"
                      value={twEndTime}
                      onChange={(e) => setTwEndTime(e.target.value)}
                      className="input-base w-full text-sm"
                      disabled={twDays.length === 0}
                    />
                  </div>
                </div>

                {/* Preview */}
                {formData.time_window && (
                  <div className="text-xs text-gray-600 bg-white rounded px-3 py-2 border border-gray-200">
                    Active: <span className="font-mono font-medium">{formData.time_window}</span>
                  </div>
                )}
                {!formData.time_window && (
                  <p className="text-xs text-gray-500">No time restriction — rule applies at all times</p>
                )}
              </div>
              {errors.time_window && (
                <p className="text-xs text-red-600 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" />{errors.time_window}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Action */}
        <div className="border-t border-gray-100 pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Target className="w-4 h-4 mr-2 text-green-500" />
            {isDgScope ? 'Access Action' : 'Routing Action'}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Action Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.action_type}
                onChange={(e) => handleChange('action_type', e.target.value)}
                className="input-base w-full"
                required
              >
                {isDgScope ? (
                  defaultAction === 'deny' ? (
                    <>
                      <option value="allow">Allow Traffic</option>
                      <option value="drop">Drop Traffic</option>
                      <option value="rate_limit">Rate Limit</option>
                    </>
                  ) : (
                    <>
                      <option value="drop">Drop Traffic</option>
                      <option value="rate_limit">Rate Limit</option>
                    </>
                  )
                ) : (
                  <>
                    <option value="route_via_interface">Route via Interface</option>
                    <option value="route_via_nexthop">Route via Next Hop</option>
                    <option value="route_via_tunnel">Route via Tunnel</option>
                    <option value="drop">Drop Traffic</option>
                    <option value="rate_limit">Rate Limit</option>
                    <option value="load_balance">Load Balance (ECMP)</option>
                  </>
                )}
              </select>
              {isDgScope && (
                <p className="text-xs text-gray-500 flex items-center">
                  <Info className="w-3 h-3 mr-1" />
                  Traffic not matched by any rule is {defaultAction === 'deny' ? <strong className="text-red-600 ml-0.5">blocked</strong> : <strong className="text-green-600 ml-0.5">allowed</strong>}
                </p>
              )}
            </div>

            {!isDgScope && <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Action Target {!isTemplate && requiresTarget && <span className="text-red-500">*</span>}
              </label>
              {isTemplate ? (
                <>
                  <input
                    type="text"
                    value="Set when applied to Edge"
                    disabled
                    className="input-base w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500">Configured per edge when template is applied</p>
                </>
              ) : formData.action_type === 'route_via_interface' ? (
                <>
                  <select
                    value={formData.action_target}
                    onChange={(e) => handleChange('action_target', e.target.value)}
                    className={`input-base w-full ${errors.action_target ? 'border-red-300 focus:ring-red-500' : ''}`}
                    required={requiresTarget}
                    disabled={destinationsLoading}
                  >
                    <option value="">Select Interface</option>
                    {edgeId && routingInterfaces.length > 0 ? (
                      routingInterfaces.map((iface) => (
                        <option key={iface.id} value={iface.portName}>
                          {iface.name} ({iface.portName} · {iface.role}){iface.ipv4 ? ` — ${iface.ipv4}` : ''}
                        </option>
                      ))
                    ) : !edgeId ? (
                      <option value="" disabled>Save and assign to edge first</option>
                    ) : destinationsLoading ? (
                      <option value="" disabled>Loading interfaces...</option>
                    ) : (
                      <option value="" disabled>No interfaces found</option>
                    )}
                  </select>
                  <p className="text-xs text-gray-500">
                    Physical port name sent to edge agent
                    {destinationsLoading && <Loader2 className="w-3 h-3 inline ml-1 animate-spin" />}
                  </p>
                </>
              ) : formData.action_type === 'route_via_tunnel' ? (
                <>
                  <select
                    value={formData.action_target}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.startsWith('nexthop:')) {
                        // Peer selected — switch to nexthop action with the peer's tunnel IP
                        setFormData(prev => ({
                          ...prev,
                          action_type: 'route_via_nexthop',
                          action_target: v.replace('nexthop:', ''),
                        }));
                      } else {
                        handleChange('action_target', v);
                      }
                    }}
                    className={`input-base w-full ${errors.action_target ? 'border-red-300 focus:ring-red-500' : ''}`}
                    required={requiresTarget}
                  >
                    <option value="">Select Tunnel</option>
                    <optgroup label="Tunnels">
                      {availableTunnels.map(t => (
                        <option key={t.name} value={t.name} disabled={!t.configured}>
                          {t.label}{t.configured ? '' : ' (not configured)'}
                        </option>
                      ))}
                    </optgroup>
                    {destinations?.peers && destinations.peers.length > 0 && (
                      <optgroup label="Peering Peers (switches to Nexthop)">
                        <option value="wg2">Any Peer (wg2)</option>
                        {destinations.peers.map(p => (
                          <option key={p.id} value={`nexthop:${p.tunnelIp}`}>
                            {p.edgeName} ({p.tunnelIp} via wg2)
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  <p className="text-xs text-gray-500">Tunnel interface for routing</p>
                </>
              ) : formData.action_type === 'route_via_nexthop' ? (
                <>
                  <input
                    type="text"
                    value={formData.action_target}
                    onChange={(e) => handleChange('action_target', e.target.value)}
                    placeholder="e.g., 192.168.1.1"
                    className={`input-base w-full ${errors.action_target ? 'border-red-300 focus:ring-red-500' : ''}`}
                    required={requiresTarget}
                  />
                  <p className="text-xs text-gray-500">IPv4 address of next hop gateway</p>
                </>
              ) : (
                <>
                  <input
                    type="text"
                    value={formData.action_target}
                    onChange={(e) => handleChange('action_target', e.target.value)}
                    placeholder="N/A for this action type"
                    className={`input-base w-full ${errors.action_target ? 'border-red-300 focus:ring-red-500' : ''}`}
                    disabled={!requiresTarget && formData.action_type !== 'rate_limit'}
                  />
                  <p className="text-xs text-gray-500">
                    {formData.action_type === 'drop' && 'Not applicable'}
                    {formData.action_type === 'rate_limit' && 'Optional description'}
                  </p>
                </>
              )}
              {errors.action_target && (
                <p className="text-xs text-red-600 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" />{errors.action_target}
                </p>
              )}
            </div>}

            {!isDgScope && <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">QoS Profile</label>
              <select
                value={formData.qos_profile || ''}
                onChange={(e) => handleChange('qos_profile', e.target.value || undefined)}
                className="input-base w-full"
              >
                <option value="">No QoS</option>
                <option value="realtime">Realtime (Lowest Latency)</option>
                <option value="priority">Priority (High Priority)</option>
                <option value="best_effort">Best Effort (Normal)</option>
              </select>
            </div>}

            {/* Bandwidth Limit — visible for DG rate_limit too */}
            {(requiresBandwidth || !isDgScope) && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">
                Bandwidth Limit (Mbps) {requiresBandwidth && <span className="text-red-500">*</span>}
              </label>
              <input
                type="number"
                value={formData.bandwidth_limit || ''}
                onChange={(e) => handleChange('bandwidth_limit', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="e.g., 100"
                className={`input-base w-full ${errors.bandwidth_limit ? 'border-red-300 focus:ring-red-500' : ''}`}
                min="1"
                required={requiresBandwidth}
                disabled={!requiresBandwidth && formData.action_type !== 'route_via_interface'}
              />
              {errors.bandwidth_limit ? (
                <p className="text-xs text-red-600 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" />{errors.bandwidth_limit}
                </p>
              ) : (
                <p className="text-xs text-gray-500">
                  {requiresBandwidth ? 'Required for rate limiting' : 'Optional bandwidth cap'}
                </p>
              )}
            </div>
            )}
            {!isDgScope && <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Fallback Target</label>
              {isTemplate ? (
                <>
                  <input
                    type="text"
                    value="Set when applied to Edge"
                    disabled
                    className="input-base w-full bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500">Configured per edge when template is applied</p>
                </>
              ) : (
                <>
                  <select
                    value={formData.fallback_target || ''}
                    onChange={(e) => handleChange('fallback_target', e.target.value || undefined)}
                    className="input-base w-full"
                  >
                    <option value="">No Fallback</option>
                    {routingInterfaces.length > 0 && (
                      <optgroup label="Physical Interfaces">
                        {routingInterfaces.map(iface => (
                          <option key={iface.id} value={iface.portName}>
                            {iface.name} ({iface.portName} · {iface.role})
                          </option>
                        ))}
                      </optgroup>
                    )}
                    <optgroup label="Tunnels">
                      {availableTunnels.filter(t => t.configured).map(t => (
                        <option key={t.name} value={t.name}>{t.label}</option>
                      ))}
                    </optgroup>
                  </select>
                  <p className="text-xs text-gray-500">Optional secondary interface/tunnel if primary is unavailable</p>
                </>
              )}
            </div>}
          </div>

          {/* Load Balance Configuration */}
          {isLoadBalance && !isDgScope && (
            <div className="mt-6 bg-gray-50 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h5 className="text-sm font-medium text-gray-700">Load Balance Interfaces</h5>
                <button
                  type="button"
                  onClick={addLBInterface}
                  className="inline-flex items-center space-x-1 text-xs text-primary-600 hover:text-primary-700"
                >
                  <Plus className="w-3 h-3" />
                  <span>Add Interface</span>
                </button>
              </div>

              {lbInterfaces.length === 0 && (
                <p className="text-xs text-amber-600">Add at least 2 interfaces for load balancing.</p>
              )}

              {lbInterfaces.map((iface: any, index: number) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className="flex-1">
                    {!isTemplate && edgeId && routingInterfaces.length > 0 ? (
                      <select
                        value={iface.name}
                        onChange={(e) => updateLBInterface(index, 'name', e.target.value)}
                        className="input-base w-full text-sm"
                      >
                        <option value="">Select Interface</option>
                        {routingInterfaces.map((ri) => (
                          <option key={ri.id} value={ri.portName}>
                            {ri.name} ({ri.portName} · {ri.role}){ri.ipv4 ? ` — ${ri.ipv4}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={iface.name}
                        onChange={(e) => updateLBInterface(index, 'name', e.target.value)}
                        placeholder="Interface name (e.g., G0)"
                        className="input-base w-full text-sm"
                      />
                    )}
                  </div>
                  <div className="w-24">
                    <input
                      type="number"
                      value={iface.weight}
                      onChange={(e) => updateLBInterface(index, 'weight', parseInt(e.target.value) || 1)}
                      min="1"
                      max="255"
                      className="input-base w-full text-sm"
                      title="Weight (1-255)"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeLBInterface(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}

              <div className="space-y-2">
                <label className="block text-xs font-medium text-gray-600">Algorithm</label>
                <select
                  value={lbAlgorithm}
                  onChange={(e) => updateLoadBalanceConfig(lbInterfaces, e.target.value)}
                  className="input-base w-full text-sm"
                >
                  <option value="round_robin">Round Robin</option>
                  <option value="weighted">Weighted</option>
                  <option value="random">Random</option>
                </select>
              </div>

              {errors.load_balance_config && (
                <p className="text-xs text-red-600 flex items-center">
                  <AlertTriangle className="w-3 h-3 mr-1" />{errors.load_balance_config}
                </p>
              )}
            </div>
          )}
        </div>

        {/* DSCP Classification — hidden for DG access policies */}
        {!isDgScope && <div className="border-t border-gray-100 pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Filter className="w-4 h-4 mr-2 text-purple-500" />
            DSCP Classification
            {(formData.dscp_mark !== undefined && formData.dscp_mark !== null) && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                Requires Suricata
              </span>
            )}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">DSCP Match</label>
              <input
                type="text"
                value={formData.dscp_match || ''}
                onChange={(e) => handleChange('dscp_match', e.target.value || undefined)}
                placeholder="e.g., 46, EF, 34-46"
                className="input-base w-full"
              />
              <p className="text-xs text-gray-500">Match traffic by DSCP value (numeric, range, or PHB name)</p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">DSCP Mark</label>
              <select
                value={formData.dscp_mark ?? ''}
                onChange={(e) => handleChange('dscp_mark', e.target.value ? parseInt(e.target.value) : undefined)}
                className="input-base w-full"
              >
                <option value="">No DSCP Marking</option>
                <option value="46">EF (46) - Expedited Forwarding / VoIP</option>
                <option value="34">AF41 (34) - Video Conference</option>
                <option value="26">AF31 (26) - Streaming Media</option>
                <option value="18">AF21 (18) - Business Data</option>
                <option value="10">AF11 (10) - Bulk Data</option>
                <option value="8">CS1 (8) - Scavenger</option>
                <option value="0">BE (0) - Best Effort</option>
              </select>
              <p className="text-xs text-gray-500">Stamp outgoing packets with this DSCP value (requires Suricata)</p>
            </div>
          </div>
        </div>}

        {/* Rule Settings */}
        <div className="border-t border-gray-100 pt-6">
          <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center">
            <Zap className="w-4 h-4 mr-2 text-yellow-500" />
            Rule Settings
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Rule Order</label>
              <input
                type="number"
                value={formData.rule_order || ''}
                onChange={(e) => handleChange('rule_order', e.target.value ? parseInt(e.target.value) : undefined)}
                placeholder="Auto-assigned if empty"
                className="input-base w-full"
                min="1"
              />
              <p className="text-xs text-gray-500">
                Lower values evaluated first (auto-assigned if empty)
              </p>
            </div>

            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Status</label>
              <select
                value={formData.enabled}
                onChange={(e) => handleChange('enabled', parseInt(e.target.value))}
                className="input-base w-full"
              >
                <option value="1">Enabled</option>
                <option value="0">Disabled</option>
              </select>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 border-l-4 border-blue-400 p-4">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> Rules are evaluated in order. First matching rule wins.
            If no criteria is specified, the rule matches all traffic.
          </p>
        </div>
      </form>
    </Modal>
  );
};

export default RuleModal;
